import React, { useState } from 'react';
import { apiClient } from '../../lib/api/client';
import { useToast } from '../ui/Toast';

interface EmailButtonProps {
  candidateId: string;
  requirementId: string;
  candidateName: string;
  candidateEmail: string;
  onEmailSent?: (result: any) => void;
}

export const EmailButton: React.FC<EmailButtonProps> = ({
  candidateId,
  requirementId,
  candidateName,
  candidateEmail,
  onEmailSent
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { toast } = useToast();

  const sendJDEmail = async () => {
    setIsLoading(true);
    
    try {
      const response = await apiClient.sendJDEmail({
        candidate_id: candidateId,
        requirement_id: requirementId
      });
      
      if (response.success) {
        setEmailSent(true);
        toast({
          title: "JD Email Sent",
          description: `Professional JD email sent to ${candidateName} at ${candidateEmail}`,
          variant: "success",
          duration: 5000
        });
        onEmailSent?.(response);
      } else {
        throw new Error(response.error || 'Failed to send email');
      }
    } catch (error: any) {
      toast({
        title: "Email Failed",
        description: error.message || 'Failed to send JD email. Please try again.',
        variant: "error",
        duration: 5000
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={sendJDEmail}
      disabled={isLoading || emailSent}
      className={`
        flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all
        ${emailSent 
          ? 'bg-green-100 text-green-700 border border-green-200' 
          : 'bg-blue-100 text-blue-700 border border-blue-200 hover:bg-blue-200'
        }
        ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      {isLoading ? (
        <>
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700"></div>
          Sending...
        </>
      ) : emailSent ? (
        <>
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          Email Sent
        </>
      ) : (
        <>
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
          </svg>
          Send JD Email
        </>
      )}
    </button>
  );
};
